import React, { useCallback, useMemo, useRef, useState } from 'react';
import { Box, Dialog, Divider, IconButton, Tooltip, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import MyLocationIcon from '@mui/icons-material/MyLocation';
import SatelliteIcon from '@mui/icons-material/Satellite';
import Map, { Layer, MapRef, Source } from 'react-map-gl';
import RoutePlannerLayers from './map/RoutePlannerLayers';
import RoutePlannerPanel from './panel/RoutePlannerPanel';
import RouteReferenceDock from './dock/RouteReferenceDock';
import { StoredIntel, persistablePayload, plannerIntel } from './dock/intel';
import { useRoutePlannerController, waypointFromCoords } from './useRoutePlannerController';
import { useMapAutoResize } from '../../hooks/useMapAutoResize';
import { geometryKey, withRouteName } from './io/missionRoute';
import { RouteProfile, RouteResult, Waypoint } from './types';
import { Mission } from '../../services/missionApi';
import { createdNamedRoute, namedRouteSaveLabel, useNamedRouteEditor, waypointsForEditor } from '../missions/namedRouteEditor';
import MapStylePopup, { MapType } from '@signals/pathfinder-ui/components/pathfinder/MapStylePopup';

const MAP_STYLE_URL: Record<MapType, string> = {
  streets: 'mapbox://styles/mapbox/streets-v12',
  satellite: 'mapbox://styles/mapbox/satellite-v9',
  'satellite-streets': 'mapbox://styles/mapbox/satellite-streets-v12',
  dark: 'mapbox://styles/mapbox/dark-v11',
  light: 'mapbox://styles/mapbox/light-v11',
  outdoors: 'mapbox://styles/mapbox/outdoors-v12',
};

export interface RoutePlannerDialogProps {
  open: boolean;
  title: string;
  onClose: () => void;
  missionId?: number | null;
  mission?: Mission | null;
  initialWaypoints?: Array<{ longitude: number; latitude: number; label?: string; notes?: string }>;
  initialProfile?: RouteProfile;
  initialPrimaryName?: string;
  initialAlternateName?: string;
  restoreAlternateKey?: string | null;
  center?: { longitude: number; latitude: number } | null;
  /** Watched areas drawn as context while planning. */
  geofences?: Array<{ name: string; coordinates: number[][] }>;
  saveLabel?: string;
  saving?: boolean;
  /** Findings already stored on the mission, shown until a fresh scan runs. */
  storedIntel?: StoredIntel | null;
  storedIntelStale?: boolean;
  onSave?: (payload: {
    waypoints: Waypoint[];
    route: RouteResult | null;
    /** The pre-vetted fallback, if the operator kept one. */
    alternate: RouteResult | null;
    alternateName: string;
    profile: RouteProfile;
    options: Record<string, unknown>;
    /** Sections worth persisting alongside the route. */
    intel: StoredIntel;
    namedSlot?: { id: string; live: boolean; draft: boolean; label: string };
  }) => Promise<Mission | void> | Mission | void;
  onActivateNamedRoute?: (id: string) => void;
  onDeleteNamedRoute?: (id: string) => void;
}

/**
 * Full-screen planner for hosts that have no map of their own to lend —
 * the Intel Packet mission tab, for instance.
 */
const RoutePlannerDialog: React.FC<RoutePlannerDialogProps> = ({
  open,
  title,
  onClose,
  missionId,
  mission,
  initialWaypoints,
  initialProfile,
  initialPrimaryName,
  initialAlternateName,
  restoreAlternateKey,
  center,
  geofences,
  saveLabel = 'Save route',
  saving,
  storedIntel,
  storedIntelStale,
  onSave,
  onActivateNamedRoute,
  onDeleteNamedRoute,
}) => {
  const mapRef = useRef<MapRef | null>(null);
  const mapBoxRef = useRef<HTMLDivElement | null>(null);
  const [hoverPoint, setHoverPoint] = useState<{ longitude: number; latitude: number } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [mapStyle, setMapStyle] = useState<MapType>('dark');
  const [styleOpen, setStyleOpen] = useState(false);
  const token = process.env.REACT_APP_MAPBOX_TOKEN || '';

  // Collapsing the dock hands its width back to the map, which Mapbox will not
  // notice on its own.
  useMapAutoResize(mapBoxRef, useCallback(() => mapRef.current?.getMap(), []), open);

  const named = useNamedRouteEditor(open ? mission : null);

  const seeded = useMemo(
    () => waypointsForEditor(named.editingId, named.editing, initialWaypoints || []).map(
      (w) => waypointFromCoords(w.longitude, w.latitude, w.label || undefined, 'mission', w.notes || undefined),
    ),
    [initialWaypoints, named.editingId, named.editing],
  );

  const planner = useRoutePlannerController({
    active: open,
    sessionKey: open ? `${missionId ?? 'open'}:${named.editingId}` : 'closed',
    onExit: onClose,
    getMap: useCallback(() => mapRef.current?.getMap(), []),
    initialWaypoints: seeded,
    initialOptions: initialProfile ? { profile: initialProfile } : undefined,
    initialPrimaryName: named.editing?.label || initialPrimaryName,
    initialAlternateName: named.editing?.alternate?.label || initialAlternateName,
    restoreAlternateKey: named.editing?.alternate?.geometry
      ? geometryKey(named.editing.alternate.geometry)
      : restoreAlternateKey,
    onError: setError,
  });

  const fenceData = useMemo<GeoJSON.FeatureCollection>(() => ({
    type: 'FeatureCollection',
    features: (geofences || [])
      .filter((f) => f.coordinates.length > 2)
      .map((f) => ({
        type: 'Feature' as const,
        properties: { label: f.name },
        geometry: { type: 'Polygon' as const, coordinates: [f.coordinates] },
      })),
  }), [geofences]);

  const intel = useMemo(
    () => plannerIntel(
      planner.state,
      { risk: planner.risk, assets: planner.assets, events: planner.events, weather: planner.weather },
      storedIntel,
      storedIntelStale,
    ),
    [planner.state, planner.risk, planner.assets, planner.events, planner.weather, storedIntel, storedIntelStale],
  );

  const anchor = seeded[0] || center;
  const initialView = {
    longitude: anchor?.longitude ?? -82.4584,
    latitude: anchor?.latitude ?? 27.9475,
    zoom: 11,
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={false}
      fullWidth
      // Planning is three things side by side — stops, map, reference — and a
      // dialog capped at `lg` gave the map whatever was left over, which on a
      // laptop was a strip too narrow to drop a route on.
      PaperProps={{ sx: { width: '98vw', maxWidth: '98vw', height: '94vh', m: 0 } }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', px: 2, py: 1.25 }}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="subtitle2" noWrap sx={{ fontWeight: 800 }}>Route planner</Typography>
          <Typography variant="caption" color="text.secondary" noWrap>
            {title} · click the map to drop stops, drag the line to pull in a via
          </Typography>
        </Box>
        <IconButton size="small" onClick={onClose}><CloseIcon fontSize="small" /></IconButton>
      </Box>
      <Divider />
      <Box sx={{ display: 'flex', flex: 1, minHeight: 0, overflow: 'hidden' }}>
        <Box sx={{ width: 320, flexShrink: 0, overflow: 'auto', p: 1.5, borderRight: '1px solid', borderColor: 'divider' }}>
          {error && (
            <Typography variant="caption" color="error" sx={{ display: 'block', mb: 1 }}>{error}</Typography>
          )}
          <RoutePlannerPanel
            state={planner.state}
            dispatch={planner.dispatch}
            canUndo={planner.canUndo}
            canRedo={planner.canRedo}
            proximity={planner.proximity}
            missionId={missionId}
            routeName={title}
            saving={saving}
            onSave={onSave
              ? async () => {
                const previousIds = new Set((mission?.saved_routes || []).map((item) => item.id));
                const draftId = named.editing?.draft ? named.editing.id : null;
                const draftLabel = named.editing?.label;
                const result = await onSave({
                  waypoints: planner.state.waypoints,
                  route: planner.primaryRoute,
                  alternate: planner.alternateRoute,
                  alternateName: planner.state.alternateName,
                  profile: planner.state.options.profile,
                  options: withRouteName({
                    exclude: planner.state.options.exclude,
                    depart_at: planner.state.options.departAt,
                  }, planner.state.primaryName),
                  intel: persistablePayload(intel),
                  namedSlot: named.editing
                    ? {
                      id: named.editing.id,
                      live: named.editing.live,
                      draft: named.editing.draft,
                      label: planner.state.primaryName.trim() || named.editing.label,
                    }
                    : undefined,
                });
                if (draftId) {
                  named.dropDraft(draftId);
                  const created = createdNamedRoute(previousIds, result?.saved_routes)
                    || (result?.saved_routes || []).find((item) => item.label === draftLabel);
                  if (created) named.setEditingId(created.id);
                }
                planner.dispatch({ type: 'MARK_SAVED', savedRouteId: null });
              }
              : undefined}
            saveLabel={namedRouteSaveLabel(named.editing, saveLabel)}
            onRetry={planner.retry}
            onFocusWaypoint={planner.focusWaypoint}
            onFlyTo={planner.flyTo}
            namedSlots={named.slots}
            editingSlotId={named.editingId}
            canAddNamedSlot={named.canAdd}
            onSelectNamedSlot={(id) => {
              if (planner.state.dirty && id !== named.editingId) {
                setError('Save this route before switching to another.');
                return;
              }
              setError(null);
              named.setEditingId(id);
              const slot = named.slots.find((item) => item.id === id) || null;
              const stops = waypointsForEditor(id, slot, initialWaypoints || []);
              planner.dispatch({
                type: 'RESET',
                waypoints: stops.map((w) => waypointFromCoords(
                  w.longitude, w.latitude, w.label || undefined, 'mission', w.notes || undefined,
                )),
                primaryName: slot?.label || '',
                alternateName: slot?.alternate?.label || '',
                restoreAlternateKey: slot?.alternate?.geometry
                  ? geometryKey(slot.alternate.geometry)
                  : null,
              });
            }}
            onAddNamedSlot={() => {
              if (planner.state.dirty) {
                setError('Save this route before adding another.');
                return;
              }
              setError(null);
              const slot = named.addSlot();
              planner.dispatch({
                type: 'RESET',
                waypoints: [],
                primaryName: slot?.label || 'Secondary',
                alternateName: '',
                restoreAlternateKey: null,
              });
            }}
            onUseNamedSlot={onActivateNamedRoute}
            onDeleteNamedSlot={(id) => {
              const slot = named.slots.find((item) => item.id === id);
              if (slot?.draft) {
                named.dropDraft(id);
                return;
              }
              onDeleteNamedRoute?.(id);
            }}
          />
        </Box>
        <Box
          ref={mapBoxRef}
          // A real flex basis rather than `flex: 1`. With a basis of zero the
          // map only ever gets the leftovers and shrinks to nothing; with one,
          // an overflowing row takes width off the map and the dock in
          // proportion, and the minimum stops it going below usable.
          sx={{ flex: '1 1 480px', minWidth: 320, position: 'relative' }}
        >
          {token ? (
            <Map
              ref={mapRef}
              mapboxAccessToken={token}
              initialViewState={initialView}
              mapStyle={MAP_STYLE_URL[mapStyle]}
              cursor="crosshair"
              onClick={(e) => planner.handleMapClick({ lng: e.lngLat.lng, lat: e.lngLat.lat })}
              style={{ width: '100%', height: '100%' }}
            >
              <Source id="route-planner/geofences" type="geojson" data={fenceData}>
                <Layer
                  id="route-planner/geofence-fill"
                  type="fill"
                  paint={{ 'fill-color': '#00bcd4', 'fill-opacity': 0.14 }}
                />
                <Layer
                  id="route-planner/geofence-line"
                  type="line"
                  paint={{ 'line-color': '#00bcd4', 'line-width': 1.5, 'line-opacity': 0.9 }}
                />
              </Source>
              <RoutePlannerLayers
                state={planner.state}
                legOffsets={planner.legOffsets}
                dispatch={planner.dispatch}
                hoverPoint={hoverPoint}
                // Held findings are indexed against the route they were run on,
                // so painting them on a newer line would misplace them.
                risk={planner.risk.stale ? null : planner.risk.report}
              />
            </Map>
          ) : (
            <Box sx={{ p: 3 }}>
              <Typography variant="body2" color="text.secondary">
                Set REACT_APP_MAPBOX_TOKEN to use the route planner.
              </Typography>
            </Box>
          )}
          {token && (
            <Box
              sx={{
                position: 'absolute',
                top: 12,
                right: 12,
                zIndex: 2,
                display: 'flex',
                flexDirection: 'column',
                gap: 0.75,
              }}
            >
              <Tooltip title="Map style">
                <IconButton
                  size="small"
                  aria-label="Change map style"
                  onClick={() => setStyleOpen((open) => !open)}
                  sx={{
                    bgcolor: 'background.paper',
                    border: '1px solid',
                    borderColor: 'divider',
                    '&:hover': { bgcolor: 'background.paper' },
                  }}
                >
                  <SatelliteIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="My location">
                <IconButton
                  size="small"
                  aria-label="Fly to my location"
                  onClick={() => {
                    navigator.geolocation?.getCurrentPosition((pos) => {
                      planner.flyTo(pos.coords.longitude, pos.coords.latitude, 14);
                    });
                  }}
                  sx={{
                    bgcolor: 'background.paper',
                    border: '1px solid',
                    borderColor: 'divider',
                    '&:hover': { bgcolor: 'background.paper' },
                  }}
                >
                  <MyLocationIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
          )}
          <MapStylePopup
            open={styleOpen}
            onClose={() => setStyleOpen(false)}
            selectedMapType={mapStyle}
            onMapTypeChange={setMapStyle}
          />
        </Box>
        <RouteReferenceDock
          intel={intel}
          storageKey="route-dock-dialog"
          hasRoute={!!planner.primaryRoute}
          hasDepartAt={!!planner.state.options.departAt}
          geometry={planner.primaryRoute?.geometry || null}
          onSelectRoute={(index) => planner.dispatch({ type: 'SET_PRIMARY', index })}
          onSetAlternate={(index) => planner.dispatch({ type: 'SET_ALTERNATE', index })}
          alternateIndex={planner.state.alternateIndex}
          primaryName={planner.state.primaryName}
          alternateName={planner.state.alternateName}
          onHoverRoute={(index) => planner.dispatch({ type: 'HOVER_ROUTE', index })}
          onFlyToStep={planner.flyToStep}
          onHoverElevation={setHoverPoint}
          onFocusAsset={planner.focusAsset}
          onFocusEvent={planner.focusEvent}
          onFocusAlert={(lat, lon) => planner.flyTo(lon, lat)}
          onFocusSegment={planner.focusSegment}
          placesRadiusM={planner.assets.radiusM}
          onPlacesRadiusChange={planner.assets.setRadiusM}
          eventsCorridorM={planner.events.corridorM}
          onEventsCorridorChange={planner.events.setCorridorM}
          eventsHorizonHours={planner.events.horizonHours}
          onEventsHorizonChange={planner.events.setHorizonHours}
          eventsWindowStart={planner.events.windowStart}
          eventsWindowEnd={planner.events.windowEnd}
          onEventsWindowStartChange={planner.events.setWindowStart}
          onEventsWindowEndChange={planner.events.setWindowEnd}
          corridorSettings={planner.risk.settings}
          onCorridorSettingsChange={planner.risk.setSettings}
        />
      </Box>
    </Dialog>
  );
};

export default RoutePlannerDialog;
